import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, ShieldCheck, UserPlus } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useToast } from '@/hooks/use-toast'
import { relativeTime } from '@/pages/user/ottoPortal'

// Every active platform admin is Otto AI staff: they can connect Claude to
// Otto and reach every client. This page lists them and turns ONE person's
// Otto access off or back on — they stay a Booked Rides admin either way.
// Rows come from the miles-portal edge function (otto_admins_list), which
// merges Booked Rides' admin list with Otto's switch-offs and connections.

interface OttoAdminRow {
  brUserId: string
  email: string
  name: string | null
  brActive: boolean
  ottoAccess: 'on' | 'off'
  switchedOffBy: string | null
  switchedOffAt: string | null
  connectedSince: string | null
  lastActivityAt: string | null
  isYou: boolean
}

interface PendingSwitch {
  admin: OttoAdminRow
  direction: 'off' | 'on'
}

const ERROR_TEXT: Record<string, string> = {
  forbidden: 'Only platform admins can manage Otto AI staff access.',
  cannot_switch_self: "You can't change your own Otto access. Ask another admin.",
  not_a_platform_admin: 'That person is no longer a platform admin.',
  portal_not_configured: 'The Otto AI connection is not configured for this environment.',
}

async function invokeOttoPortal<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('miles-portal', { body })
  if (error) {
    const context = (error as { context?: Response }).context
    const detail = context ? await context.json().catch(() => null) : null
    throw new Error(detail?.error ?? 'unavailable')
  }
  return data as T
}

const displayName = (admin: OttoAdminRow) => admin.name ?? admin.email

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function AccessBadge({ admin }: { admin: OttoAdminRow }) {
  if (!admin.brActive) return <Badge variant="outline">Suspended in Booked Rides</Badge>
  if (admin.ottoAccess === 'off') return <Badge variant="destructive">Switched off</Badge>
  return <Badge variant="secondary">On</Badge>
}

export function OttoAdminsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [pending, setPending] = useState<PendingSwitch | null>(null)

  const adminsQuery = useQuery({
    queryKey: ['otto-admins'],
    queryFn: () => invokeOttoPortal<{ admins: OttoAdminRow[] }>({ action: 'otto_admins_list' }),
    refetchInterval: 60_000,
    retry: 1,
  })

  const switchMutation = useMutation({
    mutationFn: ({ admin, direction }: PendingSwitch) =>
      invokeOttoPortal<Record<string, unknown>>({
        action: direction === 'off' ? 'otto_admin_switch_off' : 'otto_admin_switch_on',
        targetBrUserId: admin.brUserId,
      }),
    onSuccess: (_result, { admin, direction }) => {
      toast({
        title: direction === 'off' ? 'Otto access switched off' : 'Otto access turned on',
        description:
          direction === 'off'
            ? `${displayName(admin)} can no longer use Otto AI, and their Claude connection has ended. They are still a Booked Rides admin.`
            : `${displayName(admin)} can connect Claude to Otto AI again.`,
      })
      queryClient.invalidateQueries({ queryKey: ['otto-admins'] })
      setPending(null)
    },
    onError: (error: Error) =>
      toast({
        title: 'Could not change Otto access',
        description: ERROR_TEXT[error.message] ?? 'Otto is temporarily unavailable. Nothing was changed.',
        variant: 'destructive',
      }),
  })

  const admins = adminsQuery.data?.admins ?? []
  const switchingOff = pending?.direction === 'off'

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Otto Admins</h1>
          <p className="text-muted-foreground">
            Limo Marketer staff who can use Otto AI across every client. Every platform admin is included.
          </p>
        </div>
        <Button asChild>
          <Link to="/admin/users/new">
            <UserPlus className="mr-2 h-4 w-4" />
            Add admin
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Staff access
            {adminsQuery.data && <Badge variant="secondary">{admins.length}</Badge>}
          </CardTitle>
          <CardDescription>
            {adminsQuery.isError ? (
              <>
                {ERROR_TEXT[adminsQuery.error.message] ?? 'This list is temporarily unavailable.'}{' '}
                <button className="underline" onClick={() => adminsQuery.refetch()}>
                  Retry
                </button>
              </>
            ) : (
              <>
                To add someone, invite them as a user with the <strong>Admin</strong> role — they appear here once
                they accept. Switching someone off here only removes Otto AI; it doesn't change their Booked Rides
                access.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {adminsQuery.isLoading ? (
            <div className="space-y-3 py-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-3/4" />
            </div>
          ) : admins.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Admin</TableHead>
                  <TableHead>Otto access</TableHead>
                  <TableHead>Claude connected</TableHead>
                  <TableHead>Last used Otto</TableHead>
                  <TableHead className="w-px" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.brUserId}>
                    <TableCell>
                      <div className="font-medium">
                        {displayName(admin)}
                        {admin.isYou && (
                          <Badge variant="outline" className="ml-2">
                            You
                          </Badge>
                        )}
                      </div>
                      {admin.name && <div className="text-sm text-muted-foreground">{admin.email}</div>}
                    </TableCell>
                    <TableCell>
                      <AccessBadge admin={admin} />
                      {admin.ottoAccess === 'off' && admin.switchedOffAt && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          by {admin.switchedOffBy ?? 'an admin'} on {formatDate(admin.switchedOffAt)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {admin.connectedSince ? `Since ${formatDate(admin.connectedSince)}` : 'Not connected'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {admin.lastActivityAt ? relativeTime(admin.lastActivityAt) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {admin.isYou ? null : admin.ottoAccess === 'off' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={switchMutation.isPending}
                          onClick={() => setPending({ admin, direction: 'on' })}
                        >
                          Turn on
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          disabled={switchMutation.isPending}
                          onClick={() => setPending({ admin, direction: 'off' })}
                        >
                          Switch off
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : adminsQuery.data ? (
            <p className="text-sm text-muted-foreground">No platform admins found.</p>
          ) : null}

          <AlertDialog
            open={pending !== null}
            onOpenChange={(open) => !open && !switchMutation.isPending && setPending(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {switchingOff ? 'Switch off' : 'Turn on'} Otto access for {pending && displayName(pending.admin)}?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {switchingOff
                    ? 'Their Claude connection to Otto AI ends now, and they can’t reconnect until an admin turns it back on. They stay a Booked Rides admin, and nothing changes for any client.'
                    : 'They will be able to connect Claude to Otto AI again and reach every client. Their Booked Rides access is unchanged.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={switchMutation.isPending}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className={
                    switchingOff ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined
                  }
                  disabled={switchMutation.isPending}
                  onClick={(e) => {
                    e.preventDefault()
                    if (pending) switchMutation.mutate(pending)
                  }}
                >
                  {switchMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {switchingOff ? 'Switch off' : 'Turn on'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  )
}
